/**
 * Web dashboard entry (ARCHITECTURE §12 item 3, ADR-007).
 *
 * Boots the PURPOSE-BUILT web UI (src/web/app-shell.tsx, dark ink +
 * amber design language in src/web/ui/theme.css):
 *
 *   AuthProvider (session via /auth/me, login screen otherwise)
 *     └─ AppShell keyed by user id — a login/logout cycle remounts the
 *        shell, guaranteeing per-user state hygiene. The shell owns the
 *        project switcher, hash router, and screen rendering
 *        (src/web/router.tsx registry → src/web/screens/*).
 *
 * The vendored desktop App (@/App) is no longer rendered; the vendored
 * base stylesheet is kept (fonts/reset) with ./web/ui/theme.css imported
 * AFTER it so the new tokens win.
 */
import React from "react"
import ReactDOM from "react-dom/client"
import "@/index.css"
import "./web/ui/theme.css"
import { I18nextProvider } from "react-i18next"
import i18n from "./web/i18n"
import { ErrorBoundary } from "@/components/error-boundary"
import { AuthProvider, useAuth } from "./web/auth-context"
import { LoginScreen } from "./web/screens/login"
import { AppShell } from "./web/app-shell"

function Root() {
  const { status, user } = useAuth()

  if (status === "loading") {
    return (
      <div
        className="lw-root"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <span className="spinner spinner-lg" role="status" aria-label="Loading" />
      </div>
    )
  }
  if (status === "unauthenticated") {
    return <LoginScreen />
  }
  return <AppShell key={user?.id ?? "anonymous"} />
}

function initApp() {
  try {
    ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
      <React.StrictMode>
        <ErrorBoundary>
          <I18nextProvider i18n={i18n}>
            <AuthProvider>
              <Root />
            </AuthProvider>
          </I18nextProvider>
        </ErrorBoundary>
      </React.StrictMode>,
    )
  } catch (err) {
    console.error("[startup] failed to initialize LLM Wiki dashboard:", err)
    const root = document.getElementById("root")
    if (root) {
      const message =
        err instanceof Error ? (err.stack ?? err.message) : String(err)
      const escaped = message.replace(/[&<>"']/g, (ch) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[ch] ?? ch,
      )
      root.innerHTML = `
        <div style="font-family: system-ui, sans-serif; padding: 24px; min-height: 100vh; color-scheme: light dark;">
          <h1 style="font-size: 18px; margin: 0 0 12px;">LLM Wiki failed to start</h1>
          <pre style="white-space: pre-wrap;">${escaped}</pre>
        </div>
      `
    }
  }
}

initApp()
